import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { IMembersData } from '../../interfaces/businessData';
import { IRewardsContext } from '../../interfaces/rewards';
import { ITableState } from '../../interfaces/tables';
import { Rewards } from '../../services/api/rewards/rewards';
import { CompanyContext } from '../company/companyContext';

const contextDefaultValues: IRewardsContext = {
  balance: 0,
  hasCreditLine: false,
  transferData: {
    name: '',
    id: '',
    idType: '',
    accountType: '',
    accountNumber: ''
  },
  currency: 'CLP',
  countryCode: '',
  loading: false,
  error: false,
  externalId: '',
  selectedCollaborators: [],
  internalProducts: [],
  changeSelectedCollaborators: () => {
    return;
  },
  tableState: undefined,
  setTableState: () => {
    return;
  },
  updateBalance: () => {
    return;
  }
};

export const RewardsContext = createContext<IRewardsContext>(contextDefaultValues);

const RewardsContextProvider = ({ children }: React.PropsWithChildren) => {
  const [balance, setBalance] = useState(contextDefaultValues.balance);
  const [hasCreditLine, setHasCreditLine] = useState(contextDefaultValues.hasCreditLine);
  const [transferData, setTransferData] = useState(contextDefaultValues.transferData);
  const [currency, setCurrency] = useState(contextDefaultValues.currency);
  const [externalId, setExternalId] = useState(contextDefaultValues.externalId);
  const [internalProducts, setInternalProducts] = useState(contextDefaultValues.internalProducts);
  const [countryCode, setCountryCode] = useState(contextDefaultValues.countryCode);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [selectedCollaborators, setSelectedCollaborators] = useState<IMembersData[]>(
    contextDefaultValues.selectedCollaborators
  );
  const [tableState, setTableState] = useState<ITableState | undefined>(undefined);

  const { company } = useContext(CompanyContext);

  const changeSelectedCollaborators = useCallback((collaborators: IMembersData[]) => {
    setSelectedCollaborators(collaborators);
  }, []);

  const fetchRewards = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);

      const response = await Rewards.getRewardsInfo(company.id);

      setBalance(response.rewardsInfo.account.balance);
      setHasCreditLine(response.rewardsInfo.hasCreditLine);
      setTransferData(response.paymentInfo);
      setCurrency(response.rewardsInfo.account.currency);
      setExternalId(response.rewardsInfo.id);
      setInternalProducts(response.rewardsInfo.internalProducts);
      setCountryCode(response.rewardsInfo.countryCode);
    } catch {
      setError(true);

      setBalance(contextDefaultValues.balance);
      setHasCreditLine(contextDefaultValues.hasCreditLine);
      setTransferData(contextDefaultValues.transferData);
      setCurrency(contextDefaultValues.currency);
      setExternalId(contextDefaultValues.externalId);
      setInternalProducts(contextDefaultValues.internalProducts);
      setCountryCode(contextDefaultValues.countryCode);
    } finally {
      setLoading(false);
    }
  }, [company]);

  const updateBalance = useCallback((newBalance: number) => {
    setBalance(newBalance);
  }, []);

  useEffect(() => {
    fetchRewards();
  }, [fetchRewards]);

  const value = useMemo(
    () => ({
      balance,
      loading,
      transferData,
      error,
      hasCreditLine,
      currency,
      externalId,
      internalProducts,
      countryCode,
      selectedCollaborators,
      changeSelectedCollaborators,
      tableState,
      setTableState,
      updateBalance
    }),
    [
      balance,
      loading,
      transferData,
      error,
      hasCreditLine,
      currency,
      externalId,
      internalProducts,
      countryCode,
      selectedCollaborators,
      changeSelectedCollaborators,
      tableState,
      setTableState,
      updateBalance
    ]
  );

  return <RewardsContext.Provider value={value}>{children}</RewardsContext.Provider>;
};

export default RewardsContextProvider;
